import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { HashLoader } from 'react-spinners';
import { FaCopy } from "react-icons/fa6";
import { MdDelete, MdOutlineAdd, MdOutlineToggleOff, MdOutlineToggleOn } from "react-icons/md";
import ToastNotification, { showToast } from '../../components/toasts/ToastNotification';
import { MdEditSquare } from "react-icons/md";
import { MdSwitchAccount } from "react-icons/md";
import {
    deleteAccountFromProductForAdmin,
    deletePackageFromProductForAdmin,
    getProductForAdmin,
    pinProduct,
    unpinProduct,
    updateAccountProductForAdmin,
    updateTopUpProductForAdmin,
    deleteImageFromProduct,
    addImageToProduct,
    switchPackageImage,
    switchPackageStatus
} from '../../api/product.api';
import { productAttributesStatusText, productTypeText } from '../../utils';
import AddItemModal from '../../components/modal/add-item-modal';
import ConfirmModal from '../../components/modal/confirm-modal';
import './admin-edit-product.css';
import { useRef } from 'react';
import ChangePackageInformationModal from '../../components/modal/change-package-information-modal';

export const AdminEditProduct = () => {
    const { productId } = useParams();
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const [productName, setProductName] = useState("");
    const [productDescription, setProductDescription] = useState("");
    const [productPrice, setProductPrice] = useState(0);
    const [productStock, setProductStock] = useState(0);
    const [productStatus, setProductStatus] = useState("");
    const [isValuable, setIsValuable] = useState(false);
    const [imageToDelete, setImageToDelete] = useState(null);
    const switchImageRef = useRef(null);
    const [packageIdToUpdate, setPackageIdToUpdate] = useState('');
    const [packageIdToModify, setPackageIdToModify] = useState('');
    const [packageName, setPackageName] = useState('');
    const [packagePrice, setPackagePrice] = useState(0);

    const [isModalOpen, setIsModalOpen] = useState(false); // Add item modal
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false); // Delete item modal
    const [isDeleteImageModalOpen, setIsDeleteImageModalOpen] = useState(false); // Delete image modal
    const [isPackageChangeModalOpen, setIsPackageChangeModalOpen] = useState(false);

    const [selectedItemToDelete, setSelectedItemToDelete] = useState("");

    useEffect(() => {
        document.title = `Admin - ${productId}`;
        fetchProductData();
    }, [productId]);

    const fetchProductData = async () => {
        setLoading(true);
        try {
            const res = await getProductForAdmin(productId);

            if (res?.success) {
                setProduct(res.data.product);

                setProductName(res.data.product.product_name);
                setProductDescription(res.data.product.product_description);
                setProductStatus(res.data.product.product_status);
                setProductStock(res.data.product.product_stock);
                setIsValuable(res.data.product.isValuable);

                if (res.data.product.product_type === 'game_account' ||
                    res.data.product.product_type === 'utility_account' ||
                    res.data.product.product_type === 'gift_card'
                ) {
                    setProductPrice(res.data.product.product_attributes.price);
                }
            }
        }
        catch (error) {
            navigate("/super-admin/products");
            showToast(error.message, "error");
        }
        finally {
            setLoading(false);
        }
    }

    const copyToClipboard = async (text) => {
        const permission = await navigator.permissions.query({ name: "clipboard-write" });
        if (permission.state === "denied") {
            console.warn("Clipboard permission denied.");
            return;
        }

        await navigator.clipboard.writeText(text);
        showToast("Đã copy dữ liệu", "success");
    };

    const handleSwitchButtonClick = (packageId) => {
        switchImageRef.current.click();
        setPackageIdToUpdate(packageId);
    }

    const handleUpdateProduct = async () => {
        setLoading(true);
        try {
            let res = null;
            if (product?.product_type === 'game_account') {
                res = await updateAccountProductForAdmin(
                    productId,
                    productName,
                    productDescription,
                    productStatus,
                    productPrice,
                    productStock,
                    isValuable
                );
            } else if (product?.product_type === 'utility_account' || product?.product_type === 'gift_card') {
                res = await updateAccountProductForAdmin(
                    productId,
                    productName,
                    productDescription,
                    productStatus,
                    productPrice,
                    1,
                    isValuable
                );
            }
            else if (product?.product_type === 'topup_package') {
                res = await updateTopUpProductForAdmin(
                    productId,
                    productName,
                    productDescription,
                    productStatus,
                );
            }

            if (res.success) {
                showToast("Cập nhật sản phẩm thành công", "success");
                fetchProductData();
            }
        } catch (error) {
            showToast(error.message, "error");
        }
        finally {
            setLoading(false);
        }
    }

    const handleDeleteProductAttributes = async () => {
        setLoading(true);
        setIsConfirmModalOpen(false);
        try {
            let res = null;
            if (product?.product_type === 'utility_account' ||
                product?.product_type === 'game_account' ||
                product?.product_type === 'gift_card'
            ) {
                res = await deleteAccountFromProductForAdmin(productId, selectedItemToDelete);
                setProduct({
                    ...product,
                    product_attributes: {
                        ...product.product_attributes,
                        account: res.data.updatedAccounts
                    }
                })
            } else if (product?.product_type === 'topup_package') {
                res = await deletePackageFromProductForAdmin(productId, selectedItemToDelete);
                setProduct({
                    ...product,
                    product_attributes: {
                        ...product.product_attributes,
                        packages: res.data.updatedPackages
                    }
                })
            }

            if (res?.success) {
                showToast("Xóa thành công", "success");
            }
        } catch (error) {
            showToast(error.message, "error");
        }
        finally {
            setLoading(false);
        }
    }

    const handlePinProduct = async () => {
        try {
            setLoading(true);

            const res = await pinProduct(product?.productId);

            if (res.success) {
                showToast("Ghim sản phẩm thành công", "success");
                fetchProductData();
            }
        } catch (error) {
            showToast(error.message, "error");
        }
        finally {
            setLoading(false);
        }
    }

    const handleUnpinProduct = async () => {
        try {
            setLoading(true);

            const res = await unpinProduct(product?.productId);

            if (res.success) {
                showToast("Bỏ ghim sản phẩm thành công", "success");
                fetchProductData();
            }
        } catch (error) {
            showToast(error.message, "error");
        }
        finally {
            setLoading(false);
        }
    }

    const handleFileChange = async(event) => {
        const file = event.target.files[0];
        if (file) {
            try {
                setLoading(true);

                const res = await switchPackageImage(product.productId, packageIdToUpdate, file);

                if (res.success) {
                    fetchProductData();

                    showToast("Cập nhật ảnh thành công", "success");
                }
            } catch (error) {
                showToast(error.message, "error");
            }
            finally {
                setLoading(false);
            }
        }
    };

    const handleSwitchPackageStatus = async (packageId, currentStatus) => {
        const nextStatus = currentStatus === 'available' ? 'unavailable' : 'available';

        try {
            setLoading(true);

            const res = await switchPackageStatus(product?.productId, packageId, nextStatus);

            if (res?.success) {
                showToast("Cập nhật trạng thái gói nạp thành công", "success");

                if (res?.data?.product) {
                    setProduct(res.data.product);
                } else {
                    fetchProductData();
                }
            }
        } catch (error) {
            showToast(error.message, "error");
        } finally {
            setLoading(false);
        }
    }

    const handleDeleteImage = async () => {
        try {
            setLoading(true);
            setIsDeleteImageModalOpen(false);

            const res = await deleteImageFromProduct(productId, imageToDelete);

            if (res?.success) {
                showToast("Xóa ảnh thành công", "success");
                // Update product state by removing the deleted image
                setProduct({
                    ...product,
                    product_imgs: res.data.product.product_imgs
                });
            }
        } catch (error) {
            showToast(error.message, "error");
        } finally {
            setLoading(false);
        }
    }

    const handleAddImage = async (e) => {
        e.preventDefault();
        const fileInput = document.getElementById('image-upload');
        if (fileInput.files.length === 0) {
            showToast("Vui lòng chọn ảnh", "error");
            return;
        }

        const formData = new FormData();
        formData.append('product_img', fileInput.files[0]);
        formData.append('productId', productId);

        try {
            setLoading(true);
            const res = await addImageToProduct(formData);

            if (res?.success) {
                showToast("Thêm ảnh thành công", "success");
                // Update product state by adding the new image
                setProduct({
                    ...product,
                    product_imgs: res.data.product.product_imgs
                });

                // Reset file input
                fileInput.value = '';
            }
        } catch (error) {
            showToast(error.message, "error");
        } finally {
            setLoading(false);
        }
    }

    if (loading) {
        return (
            <div className="loader-container">
                <HashLoader color="#092339" />
            </div>
        )
    }

    return (
        <div className='admin-product-edit'>
            {/* Hidden file input */}
            <input
                type="file"
                accept="image/*"
                ref={switchImageRef}
                style={{ display: "none" }}
                onChange={handleFileChange}
            />
            <div className='admin-product-edit__header'>
                <span className='admin-product-edit__breadcrumb'>
                    <a href='/super-admin/products'>Danh sách sản phẩm</a> / {product?.product_name}
                </span>
            </div>

            <div className='admin-product-edit__content'>
                <div className='admin-product-edit__main-info'>
                    <div className='admin-product-edit__left'>
                        <img
                            loading='lazy'
                            className='admin-product-edit__main-image'
                            src={product?.product_imgs[0]}
                            alt='product-img'
                        />
                    </div>
                    <div className='admin-product-edit__right'>
                        <h3 className='admin-product-edit__section-title'>Thông tin sản phẩm</h3>

                        {/* Product ID */}
                        <div className='admin-product-edit__field'>
                            <input className='admin-product-edit__label' value='Mã sản phẩm' disabled />
                            <div className='admin-product-edit__input-group'>
                                <input className='admin-product-edit__input' value={product?.productId} disabled />
                                <FaCopy className='admin-product-edit__copy-icon' onClick={() => copyToClipboard(product?.productId)} />
                            </div>
                        </div>

                        {/* Product Name */}
                        <div className='admin-product-edit__field'>
                            <input className='admin-product-edit__label' value='Tên sản phẩm' disabled />
                            <div className='admin-product-edit__input-group'>
                                <input
                                    className='admin-product-edit__input'
                                    placeholder={product?.product_name}
                                    value={productName}
                                    onChange={(e) => setProductName(e.target.value)}
                                />
                            </div>
                        </div>

                        {/* Product Description */}
                        <div className='admin-product-edit__field'>
                            <input className='admin-product-edit__label' value='Mô tả' disabled />
                            <div className='admin-product-edit__input-group'>
                                <input
                                    className='admin-product-edit__input'
                                    placeholder={product?.product_description}
                                    value={productDescription}
                                    onChange={(e) => setProductDescription(e.target.value)}
                                />
                            </div>
                        </div>

                        {/* Product Price */}
                        {(product?.product_type === 'game_account' ||
                            product?.product_type === 'utility_account' ||
                            product?.product_type === 'gift_card'
                        ) &&
                            <div className='admin-product-edit__field'>
                                <input className='admin-product-edit__label' value='Giá' disabled />
                                <div className='admin-product-edit__input-group'>
                                    <input
                                        type='text'
                                        className='admin-product-edit__input'
                                        placeholder={product?.product_attributes?.price?.toLocaleString()}
                                        value={productPrice.toLocaleString('vi-VN')}
                                        onChange={(e) => setProductPrice(Number(e.target.value.replace(/\D/g, '')))}
                                    />
                                </div>
                            </div>
                        }

                        {/* Product Type */}
                        <div className='admin-product-edit__field'>
                            <input className='admin-product-edit__label' value='Loại sản phẩm' disabled />
                            <div className='admin-product-edit__input-group'>
                                <input
                                    className='admin-product-edit__input'
                                    value={productTypeText(product?.product_type)}
                                    disabled
                                />
                            </div>
                        </div>

                        {/* Product Category */}
                        <div className='admin-product-edit__field'>
                            <input className='admin-product-edit__label' value='Danh mục' disabled />
                            <div className='admin-product-edit__input-group'>
                                <input
                                    className='admin-product-edit__input'
                                    value={product?.product_category}
                                    disabled
                                />
                            </div>
                        </div>

                        {/* Product Status */}
                        <div className='admin-product-edit__field'>
                            <input className='admin-product-edit__label' value='Trạng thái' disabled />
                            <div className='admin-product-edit__input-group'>
                                <select
                                    className='admin-product-edit__select'
                                    value={productStatus}
                                    onChange={(e) => setProductStatus(e.target.value)}
                                >
                                    <option value='active'>Hoạt động</option>
                                    <option value='inactive'>Không hoạt động</option>
                                </select>
                            </div>
                        </div>

                        {/* Product Stock */}
                        {product?.product_type === 'game_account' &&
                            <div className='admin-product-edit__field'>
                                <input className='admin-product-edit__label' value='Số lượng tài khoản' disabled />
                                <div className='admin-product-edit__input-group'>
                                    <input
                                        className='admin-product-edit__input'
                                        value={product?.product_attributes?.account.length}
                                        disabled
                                    />
                                </div>
                            </div>
                        }

                        {(product?.product_type === 'utility_account' || product?.product_type === 'gift_card') &&
                            <div className='admin-product-edit__field'>
                                <input className='admin-product-edit__label' value='Số lượng tài khoản' disabled />
                                <div className='admin-product-edit__input-group'>
                                    <input
                                        className='admin-product-edit__input'
                                        value={product?.product_attributes?.account.length}
                                        disabled
                                    />
                                </div>
                            </div>
                        }

                        {product?.product_type === 'topup_package' &&
                            <div className='admin-product-edit__field'>
                                <input className='admin-product-edit__label' value='Số lượng gói nạp' disabled />
                                <div className='admin-product-edit__input-group'>
                                    <input
                                        className='admin-product-edit__input'
                                        value={product?.product_attributes?.packages?.length}
                                        disabled
                                    />
                                </div>
                            </div>
                        }

                        {/* Product Sold Amount */}
                        <div className='admin-product-edit__field'>
                            <input className='admin-product-edit__label' value='Đã bán' disabled />
                            <div className='admin-product-edit__input-group'>
                                <input
                                    className='admin-product-edit__input'
                                    value={product?.product_sold_amount}
                                    disabled
                                />
                            </div>
                        </div>

                        {/* Product Total Review */}
                        <div className='admin-product-edit__field'>
                            <input className='admin-product-edit__label' value='Tổng đánh giá' disabled />
                            <div className='admin-product-edit__input-group'>
                                <input
                                    className='admin-product-edit__input'
                                    value={product?.totalReviews}
                                    disabled
                                />
                            </div>
                        </div>

                        {/* Product Average Rating */}
                        <div className='admin-product-edit__field'>
                            <input className='admin-product-edit__label' value='Đánh giá' disabled />
                            <div className='admin-product-edit__input-group'>
                                <input
                                    className='admin-product-edit__input'
                                    value={product?.averageRating}
                                    disabled
                                />
                            </div>
                        </div>

                        {/* Manual Info Update */}
                        {((product?.product_type === 'game_account') || (product?.product_type === 'utility_account')) && (
                            <div className='admin-product-edit__field'>
                                <input className='admin-product-edit__label' value='Up thông tin thủ công' disabled />
                                <div className='admin-product-edit__input-group'>
                                    <select
                                        className='admin-product-edit__select'
                                        value={isValuable}
                                        onChange={(e) => setIsValuable(e.target.value)}
                                    >
                                        <option value={true}>Có</option>
                                        <option value={false}>Không</option>
                                    </select>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Product Images Section */}
                <div className='admin-product-edit__images'>
                    <div className='admin-product-edit__section-header'>
                        <h3 className='admin-product-edit__section-title'>Hình ảnh sản phẩm</h3>
                        <div className='admin-product-edit__add-image'>
                            <form onSubmit={handleAddImage} className='admin-product-edit__image-form'>
                                <input
                                    type='file'
                                    id='image-upload'
                                    accept='image/*'
                                    className='admin-product-edit__file-input'
                                />
                                <button type='submit' className='admin-product-edit__add-button'>
                                    <MdOutlineAdd className='admin-product-edit__add-icon' />
                                    Thêm ảnh
                                </button>
                            </form>
                        </div>
                    </div>
                    <div className='admin-product-edit__image-grid'>
                        {product?.product_imgs.map((img, index) => (
                            <div className='admin-product-edit__image-card' key={index}>
                                <img
                                    src={img}
                                    alt={`Product image ${index + 1}`}
                                    className='admin-product-edit__thumbnail'
                                />
                                <button
                                    className='admin-product-edit__delete-image'
                                    onClick={() => {
                                        setImageToDelete(img);
                                        setIsDeleteImageModalOpen(true);
                                    }}
                                >
                                    <MdDelete className='delete-icon' />
                                </button>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Product Attributes */}
                <div className='admin-product-edit__attributes'>
                    <div className='admin-product-edit__section-header'>
                        <h3 className='admin-product-edit__section-title'>
                            {productTypeText(product?.product_type)}
                        </h3>
                        {((product?.product_type === 'game_account' && !product?.isValuable) ||
                            product?.product_type === 'utility_account' ||
                            product?.product_type === 'topup_package' ||
                            product?.product_type === 'gift_card'
                        ) && (
                                <button
                                    className='admin-product-edit__add-button'
                                    onClick={() => setIsModalOpen(true)}
                                >
                                    <MdOutlineAdd className='admin-product-edit__add-icon' />
                                    Thêm mới
                                </button>
                            )}
                    </div>

                    {/* Game Account Table */}
                    {product?.product_type === 'game_account' && (
                        <div className='admin-product-edit__table-wrapper'>
                            <table className='admin-product-edit__table'>
                                <thead>
                                    <tr>
                                        <th>STT</th>
                                        <th>Tài khoản</th>
                                        <th>Mật khẩu</th>
                                        <th>Tình trạng</th>
                                        <th>Thao tác</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {!product?.isValuable ? (
                                        product?.product_attributes?.account?.map((acc, index) => (
                                            <tr key={index}>
                                                <td>{index + 1}</td>
                                                <td>{acc.username}</td>
                                                <td>{acc.password}</td>
                                                <td>{acc.sold ? "Đã bán" : "Chưa bán"}</td>
                                                <td>
                                                    <button
                                                        className='admin-product-edit__action-button'
                                                        onClick={() => {
                                                            setIsConfirmModalOpen(true);
                                                            setSelectedItemToDelete(acc._id);
                                                        }}
                                                    >
                                                        <MdDelete />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan={5} className='admin-product-edit__no-data'>
                                                Không có tài khoản
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Utility Account or Gift Card Table */}
                    {(product?.product_type === 'utility_account' || product?.product_type === 'gift_card') && (
                        <div className='admin-product-edit__table-wrapper'>
                            <table className='admin-product-edit__table'>
                                <thead>
                                    <tr>
                                        <th>STT</th>
                                        <th>Tài khoản</th>
                                        <th>Mật khẩu</th>
                                        <th>Trạng thái</th>
                                        <th>Thao tác</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {product?.product_attributes?.account?.map((acc, index) => (
                                        <tr key={index}>
                                            <td>{index + 1}</td>
                                            <td>{acc.username}</td>
                                            <td>{acc.password}</td>
                                            <td>{acc.sold ? "Đã bán" : "Chưa bán"}</td>
                                            <td>
                                                <button
                                                    className='admin-product-edit__action-button'
                                                    onClick={() => {
                                                        setIsConfirmModalOpen(true);
                                                        setSelectedItemToDelete(acc._id);
                                                    }}
                                                >
                                                    <MdDelete />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Top-up Package Table */}
                    {product?.product_type === 'topup_package' && (
                        <div className='admin-product-edit__table-wrapper'>
                            <table className='admin-product-edit__table'>
                                <thead>
                                    <tr>
                                        <th>STT</th>
                                        <th>Hình ảnh</th>
                                        <th>Tên gói nạp</th>
                                        <th>Giá</th>
                                        <th>Trạng thái</th>
                                        <th>Thao tác</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {product?.product_attributes?.packages?.map((pack, index) => (
                                        <tr key={index}>
                                            <td>{index + 1}</td>
                                            <td><img className='package-table-image' src={pack.img}></img></td>
                                            <td>{pack.name}</td>
                                            <td>{pack.price.toLocaleString()}</td>
                                            <td>{productAttributesStatusText(pack.status)}</td>
                                            <td>
                                                <div className='topup-package-action-buttons-container'>
                                                    <button
                                                        className='admin-product-modify__action-button'
                                                        onClick={() => {
                                                            setPackageIdToModify(pack._id);
                                                            setPackageName(pack.name);
                                                            setPackagePrice(pack.price);
                                                            setIsPackageChangeModalOpen(true);
                                                        }}
                                                    >
                                                        <MdEditSquare />
                                                    </button>
                                                    <button
                                                        className='admin-product-switch__action-button'
                                                        onClick={() => {
                                                            handleSwitchButtonClick(pack._id)
                                                        }}
                                                    >
                                                        <MdSwitchAccount />
                                                    </button>
                                                    <button
                                                        className='admin-product-switch__action-button'
                                                        title={pack.status === 'available' ? 'Chuyển sang Order' : 'Chuyển sang Có sẵn'}
                                                        onClick={() => handleSwitchPackageStatus(pack._id, pack.status)}
                                                    >
                                                        {pack.status === 'available' ? <MdOutlineToggleOn color='green' /> : <MdOutlineToggleOff color='red'/>}
                                                    </button>
                                                    <button
                                                        className='admin-product-edit__action-button'
                                                        onClick={() => {
                                                            setIsConfirmModalOpen(true);
                                                            setSelectedItemToDelete(pack._id);
                                                        }}
                                                    >
                                                        <MdDelete />
                                                    </button>
                                                </div>

                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Action Buttons */}
                <div className='admin-product-edit__actions'>
                    <div className='admin-product-edit__main-actions'>
                        <button onClick={handleUpdateProduct} className='admin-product-edit__update-button'>
                            Cập nhật
                        </button>
                        <button onClick={() => navigate('/super-admin/products')} className='admin-product-edit__cancel-button'>
                            Hủy bỏ
                        </button>
                    </div>

                    {product?.product_type === 'game_account' && (
                        <div className='admin-product-edit__pin-actions'>
                            {product?.product_attributes?.pin ? (
                                <button onClick={handleUnpinProduct} className='admin-product-edit__unpin-button'>
                                    Bỏ ghim sản phẩm
                                </button>
                            ) : (
                                <button onClick={handlePinProduct} className='admin-product-edit__pin-button'>
                                    Ghim sản phẩm
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>

            <ToastNotification />

            <AddItemModal
                productId={productId}
                isOpen={isModalOpen}
                selectedType={product?.product_type}
                onClose={() => setIsModalOpen(false)}
                setProduct={setProduct}
                product={product}
            />

            <ConfirmModal
                isOpen={isConfirmModalOpen}
                onConfirm={handleDeleteProductAttributes}
                onClose={() => setIsConfirmModalOpen(false)}
                message={'Bạn có chắc chắn muốn xóa mục này không?'}
                title={'Xác nhận xóa'}
            />

            <ConfirmModal
                isOpen={isDeleteImageModalOpen}
                onConfirm={handleDeleteImage}
                onClose={() => setIsDeleteImageModalOpen(false)}
                message={'Bạn có chắc chắn muốn xóa hình ảnh này không?'}
                title={'Xác nhận xóa'}
            />
            <ChangePackageInformationModal
                isOpen={isPackageChangeModalOpen}
                title={"Đổi thông tin gói nạp"}
                onClose={() => setIsPackageChangeModalOpen(false)}
                packageId={packageIdToModify}
                productId={product?.productId}
                pack_name={packageName}
                pack_price={packagePrice}
                fetchData={fetchProductData}
            />
        </div>
    );
}
